/**
 * MoreInformationSection — the ONE entry point everything the Servicer rarely
 * needs sits behind (AC-7).
 *
 * It renders THE RUN RECORD: the same `RunRecordBody` the standalone
 * `/workflows/runs/<id>` page mounts. Before this it assembled its own view
 * from `DrawerSectionMap` — client context, prior decisions, artifacts, run
 * metadata, result, detailed log — which meant ora's drawer and the record page
 * were two assemblies of the same material, drifting independently. There is
 * now one record with two mount points, and this is the drawer's.
 *
 * What stayed, because all of it is load-bearing:
 *
 * Children are not mounted until the pane has been opened. Opening replaces
 * the run body (absolute overlay) rather than expanding an accordion
 * downward — that is the EMUW Drawers "new pane" entry point.
 *
 * The overlay is `absolute inset-0`, sized to the nearest POSITIONED
 * ancestor that is NOT itself the thing scrolling. Every host must provide
 * such an ancestor above whatever container has `overflow: auto/scroll` —
 * if the scrolling element is ALSO the positioned one, the overlay scrolls
 * out of view along with everything else the moment the AM has scrolled
 * before opening it (confirmed against a standalone repro: the add-in's
 * `live-body`/`terminal-body` had `relative` on the SAME element as
 * `overflow-y-auto`, which is exactly this bug — fixed there, not here,
 * since `mode-body-wrapper` one level up is already `relative` and does not
 * scroll). This component deliberately stays a bare wrapper with no
 * position/height opinion of its own — adding one here previously made
 * things worse (it competed for flex space with its still-mounted siblings
 * and got squeezed to near-zero height instead).
 *
 * CONTROLLED, not self-managed: `open`/`onClose` are owned by the top-level
 * drawer component, not a local `useState` here. The entry-point BUTTON
 * itself lives in the sticky footer (`DrawerFooter`'s
 * `onOpenMoreInformation`), pinned alongside Cancel/Close instead of inline
 * in the scrolling body — so its position doesn't jitter as step/phase
 * updates arrive, and (the reason it must be controlled at all) the boolean
 * survives a step/phase update even if this component's own subtree is
 * ever torn down and rebuilt, instead of resetting to closed every time new
 * data arrives.
 *
 * Anything awaiting the Servicer's answer NEVER goes in here, in any status
 * family: `input_needed`, every HITL prompt and the carrier-evidence decision
 * card stay in the primary body. That is AC-7's hard rule, and the callers
 * enforce it by keeping those in the body rather than in the record.
 */
import { createContext, useContext, type ReactNode } from "react";
import { TakeoverBackHeader } from "../../components/TakeoverBackHeader";
import { RunRecordBody } from "../../runRecord/RunRecordBody";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";

export const MORE_INFORMATION_TITLE = "More information";

/**
 * Whether this pane draws its OWN `← More information` back row. ora: yes
 * (default). The Outlook pane provides `false`: its drawer shows exactly one
 * back row at the top and relabels it to the run while this pane is open
 * (2026-10-06, "only show me the one that takes me to the previous screen").
 * Context rather than a prop so the six drawer bodies that mount this section
 * need nothing threaded through them.
 */
export const MoreInformationOwnBackRowContext = createContext(true);

export interface MoreInformationSectionProps {
  /** The record to show. Built by the host from the run it already has —
   *  see RunRecordBody. Includes `extraBlocks` for the host's own
   *  CarrierHandoff / EndorsementHistory / DebugActionsPanel. */
  record: RunRecordBodyProps;
  /** Host blocks rendered inside the record — the family's `CarrierHandoff` /
   *  `EndorsementHistory` / result hero, plus `DebugActionsPanel`. Kept a
   *  separate prop rather than folded into `record` by every caller: the
   *  bodies each assemble a different set of them, and the record itself is
   *  identical across all six. */
  extraBlocks?: ReactNode;
  /** Whether the pane is open. Owned by the top-level drawer component. */
  open: boolean;
  /** Called when the pane's own back button is clicked. */
  onClose: () => void;
}

export function MoreInformationSection({
  record,
  extraBlocks,
  open,
  onClose,
}: MoreInformationSectionProps) {
  const ownBackRow = useContext(MoreInformationOwnBackRowContext);
  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col bg-base-white"
      data-testid="more-information"
      role="dialog"
      aria-label={MORE_INFORMATION_TITLE}
    >
      {/* The row itself moved to the shared TakeoverBackHeader when the pane's
          "Launch anyway" fallback needed the same chrome. Same markup, same
          test id — this component keeps the behaviour, not the copy. */}
      {ownBackRow ? (
        <TakeoverBackHeader
          label={MORE_INFORMATION_TITLE}
          onBack={onClose}
          testId="more-information-back"
        />
      ) : (
        <h3
          className="flex-shrink-0 border-b border-primary-gray-light-200 bg-base-white px-3 py-2.5 text-sm font-medium text-primary-gray-light-900"
          data-testid="more-information-heading"
        >
          {MORE_INFORMATION_TITLE}
        </h3>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <RunRecordBody
          {...record}
          extraBlocks={extraBlocks ?? record.extraBlocks}
          variant="drawer"
        />
      </div>
    </div>
  );
}

export default MoreInformationSection;
